import _ from 'lodash';

interface IEventSubscription {
    subscriptionId: string;
    subscriberName: string;
    callback: (args: any) => void;
}

/**
 * EventBusModule
 *
 * This module uses PubSub pattern to handle events
 * You can create your own EventBus or use the default `globalEventBus`
 *
 * The topic object contains all topics and each topic contains an array of subscribers (objects)
 *
 * In order to avoid name collision between topics, we recommend to include tool/service name
 * in the topic name.
 *
 * We don't support topic deletion
 * You will only be able to unsubscribe a specific subscriber from a topic.
 *
 * var eventBus = new EventBus();
 *
 * // subscribe
 * var sub1 = evenBus.on('tool.topic1', 'myTool', callbackFunction);
 *
 * // publish
 * eventBus.publish('tool.topic1', data);
 *
 * // unsubscribe using returned object
 * sub1.off();
 *
 * // unsubscribe using EventBus
 * eventBus.off(sub1);
 *
 */

export class EventBus {
    private subscriptionIdToTopicNameMap: Map<string, string>;
    private readonly topics: { [topicName: string]: IEventSubscription[] };
    private nextSubscriptionId: number;

    constructor() {
        this.subscriptionIdToTopicNameMap = new Map();
        this.topics = {};
        this.nextSubscriptionId = 1;
    }

    /**
     * Publish events with a specific topic name and arguments such as the data to pass along.
     * If a callback fails it logs the error and keeps publishing.
     *
     * @param {string} topicName The topic's name.
     * @param {*} data The data to be passed.
     */
    public publish(topicName: string, data: object) {
        let subscriptions;

        if (!this.topics[topicName]) {
            console.warn('There are no subscriptions for this topic', topicName);
            return;
        }

        subscriptions = _.clone(this.topics[topicName]);

        // We use $timeout(..., 0) to have a deterministic behavior, without blocking the main thread.
        setTimeout(() => {
            subscriptions.forEach((currentSubscription: IEventSubscription) => {
                try {
                    if (currentSubscription.callback) {
                        currentSubscription.callback({
                            data: _.clone(data),
                        });
                    }
                } catch (e) {
                    console.error('Failed to execute callback for', currentSubscription.subscriberName, e);
                }
            });
        }, 0);

        return;
    }

    /**
     * Subscribe to events with a specific topic name and a callback function,
     * to be executed when topic/event is observed.
     *
     * @param {string} topicName The topic's name.
     * @param {string} subscriberName The subscriber's name.
     * @param {function} callback Callback function to execute on event, taking one argument.
     *                            In order to avoid infinite loops and non-deterministic behavior, do not include
     *                            calls to `publish` inside your callback.
     *                   - {object} data The data passed when publishing an event.
     * @returns {Object} The subscription containing subscriptionId subscriberName and off function,
     *                      so you can just call .off() to unsubscribe.
     */
    public on(topicName: string, subscriberName: string, callback: (args: any) => void) {
        let subscriptionId;

        if (!topicName || !_.isString(topicName)) {
            throw new TypeError('Invalid topic name, it must be a non empty string.');
        }

        if (!subscriberName || !_.isString(subscriberName)) {
            throw new TypeError('Invalid subscriber name, it must be a non empty string.');
        }

        if (!callback || !_.isFunction(callback)) {
            throw new TypeError('When subscribing for an event, a callback function must be defined.');
        }

        subscriptionId = this.nextSubscriptionId++;

        if (!this.topics[topicName]) {
            this.topics[topicName] = [];
        }

        this.topics[topicName].push({
            subscriptionId,
            subscriberName,
            callback,
        });

        this.subscriptionIdToTopicNameMap.set(subscriptionId, topicName);

        return {
            subscriptionId,
            subscriberName,
            off: () => this.unsubscribeSubscriptionById(subscriptionId, this),
        };
    }

    /**
     * Unsubscribe from a topic based on subscripcion id.
     *
     * @param {Object} subscription The subscription object.
     * @returns {boolean|string} False if `subscription` does not match a subscribed event, else the topic's name.
     */
    public off(subscription: IEventSubscription) {
        // subscriber id starts at 1
        if (subscription && subscription.subscriptionId) {
            return this.unsubscribeSubscriptionById(subscription.subscriptionId, this);
        }
        return false;
    }

    /**
     * Removes the subscriber from the EventBus and subscriptionIdToTopicNameMap.
     *
     * @param {number} subscriptionId
     * @param {Object} eventBus
     * @returns {boolean|string} False if `subscriber` does not match a subscribed event, else the topic's name.
     */
    private unsubscribeSubscriptionById(subscriptionId: string, eventBus: EventBus) {
        const topicName = eventBus.subscriptionIdToTopicNameMap.get(subscriptionId);

        if (!topicName) {
            console.warn(subscriptionId, 'is not subscribed to a topic');
            return false;
        }

        const subscriptions = eventBus.topics[topicName];
        // Since we already validate that there is a topic associated with the subscriber id
        // this should always find a valid index
        const index = _.findIndex(subscriptions, function (subscription: IEventSubscription) {
            return subscription.subscriptionId === subscriptionId;
        });

        subscriptions.splice(index, 1);
        eventBus.subscriptionIdToTopicNameMap.delete(subscriptionId);
        return topicName;
    }
}

// Export singleton for global use
export const globalEventBus = new EventBus();
